import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { getPricedTicketGroups } from '@/helpers/PriceCheck';
import { getButtonLabel, reserveTicketsPayload } from '@/helpers/Reserve';
import { getSessionTicketGroupsAndTypes } from '@/helpers/SessionHelpers';
import { surveySpecsWithTicketGroupId } from '@/helpers/SurveyHelpers';
import { isForCodes, isForFlexibleTickets } from '@/helpers/TicketGroupHelpers';
import { createQuantities, sumQuantities } from '@/helpers/TicketTypeQuantities';
import { formIsValid, reportFormValidity } from '@/helpers/Validation';
import RenewMembershipMessage from '@/components/elements/RenewMembershipMessage.vue';
import AdmitDetailsFields from '@/components/events/AdmitDetailsFields.vue';
import DynamicMessageGroup from '@/components/events/DynamicMessageGroup.vue';
import EventDetailErrorMessages from '@/components/events/EventDetailErrorMessages.vue';
import SelectQuantities from '@/components/events/SelectQuantities.vue';
import SelectSeats from '@/components/events/SelectSeats.vue';
import FormInput2 from '@/components/forms/FormInput2.vue';
import Survey2 from '@/components/forms/Survey2.vue';
import { fetchFirstAvailableSession } from '@/api/Sessions';
import { languageItem } from '@/language/helpers';
import { assignSeatsByTT } from '@/seats/assignments';
import { seatSelectionNeeded } from '@/seats/helpers';
import { reserveSubmitHandler } from '@/state/Reserve';
import MobileFooterPortal from '../cart/MobileFooterPortal.vue';
import ReserveMobileFooter from '../cart/ReserveMobileFooter.vue';
@Component({
    name: 'ReserveSingleEvent',
    components: {
        SelectQuantities,
        SelectSeats,
        AdmitDetailsFields,
        FormInput2,
        Survey2,
        MobileFooterPortal,
        ReserveMobileFooter,
        RenewMembershipMessage,
        DynamicMessageGroup,
        EventDetailErrorMessages,
    },
})
export default class default_1 extends Vue {
    @Prop({ required: true })
    event;
    formId = 'reserve-single-event-form';
    @Prop()
    contexts;
    loading = true;
    submitting = false;
    apiError = null;
    session = null;
    sessionTicketGroups = null;
    selectedQuantities = {};
    selectedSeats = null;
    surveyAnswers = {};
    details = {};
    formIsValid = false;
    seatMapOpen = false;
    discountCodePurchase = false;
    l = languageItem('reserve');
    @Watch('event', { immediate: true })
    onEventChange(event) {
        this.loading = true;
        if (this.event.ticketGroups.every(isForCodes)) {
            this.loading = false;
            this.discountCodePurchase = true;
            this.sessionTicketGroups = this.event.ticketGroups;
            getPricedTicketGroups(this.event.id, this.sessionTicketGroups).then((groups) => (this.sessionTicketGroups = groups));
        }
        else {
            fetchFirstAvailableSession(event)
                .then((session) => {
                this.session = session;
                this.sessionTicketGroups = null;
                return this.getSelectedQuantities(event, session);
            })
                .then((quantities) => {
                this.selectedQuantities = quantities;
                this.loading = false;
            });
        }
    }
    getSelectedQuantities(event, session) {
        if (session) {
            const tgIDs = event.ticketGroups.map((group) => group.id);
            return getSessionTicketGroupsAndTypes(this.event.id, session.id, tgIDs).then((groups) => {
                this.sessionTicketGroups = groups;
                return createQuantities(groups, this.session, this.anchor);
            });
        }
        else {
            return Promise.resolve({});
        }
    }
    get showQuantitySelection() {
        return !this.seatMapOpen;
    }
    get anchor() {
        return this.$store.getters['Cart/anchor'];
    }
    get seatSelectionNeeded() {
        return seatSelectionNeeded(this.showSeatSelection, this.selectedSeats, this.selectedSeatedGroups, this.selectedQuantities);
    }
    get selectedSeatedGroups() {
        return this.selectedGroups?.filter((group) => group.handler === 'seated');
    }
    get selectedGroups() {
        return this.ticketGroups?.filter((group) => {
            return group.types.some((type) => this.selectedQuantities[type.id]?.quantity > 0);
        });
    }
    get surveySpecs() {
        return surveySpecsWithTicketGroupId(this.selectedGroups);
    }
    get reservePayload() {
        const result = [];
        const flexibleTTs = this.flexibleTicketTypeIDs;
        const seats = assignSeatsByTT(this.selectedSeats, this.selectedQuantities, this.selectedSeatedGroups);
        for (const [id, { quantity, price }] of Object.entries(this.selectedQuantities)) {
            const quantities = { [id]: { quantity, price } };
            // Omit the session ID for ticket types belonging to flexible-ticket ticket groups.
            const session = flexibleTTs.has(id) ? undefined : this.session;
            result.push(...reserveTicketsPayload(quantities, session, this.details, seats));
        }
        return result;
    }
    get flexibleTicketTypeIDs() {
        const result = new Set();
        for (const group of this.ticketGroups) {
            if (isForFlexibleTickets(group)) {
                for (const ticketType of group.types) {
                    result.add(ticketType.id);
                }
            }
        }
        return result;
    }
    @Watch('details', { deep: true })
    @Watch('totalSelectedTickets')
    @Watch('surveyAnswers', { deep: true })
    updateValidation() {
        // This is called in the render phase, before refs have been linked.
        // If the session and quantity are both set on initialisation then this.$refs.form is still not defined.
        this.formIsValid =
            this.totalSelectedTickets > 0 && this.$refs.form && formIsValid(this.$refs.form);
    }
    onSubmit() {
        this.submit();
    }
    submit(codes) {
        return this.validate()
            .then(() => {
            this.submitting = true;
            const submitOptions = {
                ticketsPayload: this.reservePayload,
                surveyAnswers: this.surveyAnswers,
                selectedGroups: this.selectedGroups ?? [],
                emitCallback: this.$emit.bind(this),
                codes: codes,
            };
            return reserveSubmitHandler(submitOptions);
        })
            .catch((error) => {
            this.apiError = error;
        })
            .finally(() => {
            this.submitting = false;
        });
    }
    validate() {
        return reportFormValidity(this.$refs.form);
    }
    get ticketGroups() {
        return this.sessionTicketGroups ?? [];
    }
    get totalSelectedTickets() {
        return sumQuantities(this.selectedQuantities);
    }
    get showSeatSelection() {
        return (this.selectedSeatedGroups && this.selectedSeatedGroups.length > 0) ?? false;
    }
    get giftOfMembership() {
        return this.contexts.has('gift-of-membership');
    }
    get buttonLabel() {
        return getButtonLabel(this.discountCodePurchase, this.giftOfMembership, this.totalSelectedTickets);
    }
}
